import { ColorPicker, Form, Input, InputNumber, Select } from "antd";
import React, { createRef } from "react";

function onSaveProperties(formRef, node) {
  const form = formRef.current;
  const values = form.getFieldsValue(true);

  Object.keys(values).forEach((key) => {
    node.setAttrByPath(key, values[key]);
  });
}

export default function initStencil(graph, stencil, modal) {
  const textBox = graph.createNode({
    shape: "text-box",
  });

  const rect = graph.createNode({
    shape: "rect",
    width: 40,
    height: 40,
    attrs: {
      body: {
        stroke: "var(--ant-color-text-base)",
        strokeWidth: 1,
        fill: "transparent",
      },
    },
  });

  const circle = graph.createNode({
    shape: "circle",
    width: 40,
    height: 40,
    attrs: {
      body: {
        stroke: "var(--ant-color-text-base)",
        strokeWidth: 1,
        fill: "transparent",
      },
    },
  });

  stencil.load([textBox, rect, circle], "general");

  const meterDetaiNode = graph.createNode({
    shape: "power-meter-detail",
  });

  stencil.load([meterDetaiNode], "power-meter");

  const transformer = graph.createNode({
    shape: "transformer",
  });

  const star_delta_transformer = graph.createNode({
    shape: "star-delta-transformer",
  });

  const delta_star_transformer = graph.createNode({
    shape: "delta-star-transformer",
  });

  const delta_delta_transformer = graph.createNode({
    shape: "delta-delta-transformer",
  });

  const star_star_transformer = graph.createNode({
    shape: "star-star-transformer",
  });

  stencil.load(
    [
      transformer,
      star_delta_transformer,
      delta_star_transformer,
      delta_delta_transformer,
      star_star_transformer,
    ],
    "transformers"
  );

  const ats = graph.createNode({
    shape: "automatic-transfer-switch",
  });

  stencil.load([ats], "switches");

  graph.on("blank:dblclick", ({ e }) => {
    graph.centerContent();
  });

  //Double Click Event Edit Properties
  graph.on("node:dblclick", ({ node, e }) => {
    //Text Box
    if (node.shape === "text-box") {
      let formRef = React.createRef();
      modal.confirm({
        icon: <></>,
        title: "Text Box Properties",
        okText: "Save",
        content: (
          <Form ref={formRef}>
            <br></br>
            <Form.Item
              label="Font Color"
              name="label/fill"
              initialValue={
                node.getAttrByPath("label/fill") ?? "var(--ant-color-text-base)"
              }
              getValueFromEvent={(color) => {
                return "#" + color.toHex();
              }}
            >
              <ColorPicker format={"hex"} showText></ColorPicker>
            </Form.Item>
            <Form.Item
              label="Font Size"
              name="label/fontSize"
              initialValue={node.getAttrByPath("label/fontSize") ?? 12}
            >
              <InputNumber min={1} max={40}></InputNumber>
            </Form.Item>
            <Form.Item
              label="Font Weight"
              name="label/fontWeight"
              initialValue={node.getAttrByPath("label/fontWeight") ?? null}
            >
              <Select>
                <Select.Option value={null} label="Normal">
                  Normal
                </Select.Option>
                <Select.Option
                  value={"var(--ant-font-weight-strong)"}
                  label="Bold"
                >
                  Bold
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Text Align"
              name="label/textAnchor"
              initialValue={node.getAttrByPath("label/textAnchor") ?? "middle"}
            >
              <Select
                onChange={(value) => {
                  if (value == "start") {
                    formRef.current.setFieldValue("label/refX", 2);
                  } else if (value == "middle") {
                    formRef.current.setFieldValue("label/refX", 0.5);
                  } else if (value == "end") {
                    formRef.current.setFieldsValue({
                      "label/refX": "100%",
                      "label/refX2": -2,
                    });
                  }
                }}
              >
                <Select.Option value={"start"} label="Left">
                  Left
                </Select.Option>
                <Select.Option value={"middle"} label="Center">
                  Center
                </Select.Option>
                <Select.Option value={"end"} label="Right">
                  Right
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Text Vertical Align"
              name="label/textVerticalAnchor"
              initialValue={
                node.getAttrByPath("label/textVerticalAnchor") ?? "middle"
              }
            >
              <Select
                onChange={(value) => {
                  if (value == "top") {
                    formRef.current.setFieldValue("label/refY", 2);
                  } else if (value == "middle") {
                    formRef.current.setFieldValue("label/refY", 0.5);
                  } else if (value == "bottom") {
                    formRef.current.setFieldsValue({
                      "label/refY": "100%",
                      "label/refY2": -2,
                    });
                  }
                }}
              >
                <Select.Option value={"top"} label="Top">
                  Top
                </Select.Option>
                <Select.Option value={"middle"} label="Center">
                  Center
                </Select.Option>
                <Select.Option value={"bottom"} label="Bottom">
                  Bottom
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Text"
              name="label/text"
              initialValue={node.getAttrByPath("label/text") ?? null}
            >
              <Input.TextArea />
            </Form.Item>
          </Form>
        ),
        onOk() {
          onSaveProperties(formRef, node);
        },
      });
    }
    //Rect, Circle
    else if (["rect", "circle"].includes(node.shape)) {
      let formRef = React.createRef();
      modal.confirm({
        icon: <></>,
        title: "Properties",
        okText: "Save",

        content: (
          <Form ref={formRef}>
            <br></br>
            <Form.Item
              label="Stroke Color"
              name="body/stroke"
              initialValue={
                node.getAttrByPath("body/stroke") ??
                "var(--ant-color-text-base)"
              }
              getValueFromEvent={(color) => {
                return "#" + color.toHex();
              }}
            >
              <ColorPicker format={"hex"} showText></ColorPicker>
            </Form.Item>
            <Form.Item
              label="Stroke Width"
              name="body/strokeWidth"
              initialValue={node.getAttrByPath("body/strokeWidth") ?? 1}
            >
              <InputNumber min={1} max={40}></InputNumber>
            </Form.Item>
            <Form.Item
              label="Stroke Type"
              name="body/strokeDasharray"
              initialValue={node.getAttrByPath("body/strokeDasharray") ?? 0}
            >
              <Select>
                <Select.Option value={0} label="Solid">
                  Solid
                </Select.Option>
                <Select.Option value={2} label="Dotted">
                  Dotted
                </Select.Option>
                <Select.Option value={5} label="Dashed">
                  Dashed
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Fill Color"
              name="body/fill"
              initialValue={
                node.getAttrByPath("body/fill") ?? "var(--ant-color-text-base)"
              }
              getValueFromEvent={(color) => {
                return "#" + color.toHex();
              }}
            >
              <ColorPicker format={"hex"} showText></ColorPicker>
            </Form.Item>
          </Form>
        ),
        onOk() {
          onSaveProperties(formRef, node);
        },
      });
    }
    //Transformer
    else if (
      [
        "transformer",
        "star-delta-transformer",
        "delta-star-transformer",
        "delta-delta-transformer",
        "star-star-transformer",
      ].includes(node.shape)
    ) {
      let formRef = React.createRef();
      modal.confirm({
        icon: <></>,
        title: "Transformer Properties",
        okText: "Save",
        content: (
          <Form ref={formRef}>
            <br></br>
            <Form.Item
              label="Title"
              name="title/text"
              initialValue={node.getAttrByPath("title/text")}
            >
              <Input></Input>
            </Form.Item>
            <Form.Item
              label="Label"
              name="subtitle/text"
              initialValue={node.getAttrByPath("subtitle/text")}
            >
              <Input.TextArea></Input.TextArea>
            </Form.Item>
          </Form>
        ),
        onOk() {
          onSaveProperties(formRef, node);
        },
      });
    }
  });
}
